import React, { useMemo } from 'react';
import { RadioTowerIcon, ServerIcon, MapIcon, ActivityIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { KPICard } from '../components/KPICard';
import { CardSkeleton, ChartSkeleton, TableSkeleton } from '../components/LoadingSkeleton';
export function Overview() {
  const {
    data = [],
    loading,
    error
  } = useSupabaseQuery({
    limit: 5000
  });
  const {
    data: recentData = [],
    loading: recentLoading
  } = useSupabaseQuery({
    limit: 10,
    orderBy: {
      column: 'Site_ID_NE',
      ascending: false
    }
  });
  const kpis = useMemo(() => {
    if (!data.length) return null;
    return {
      totalSites: data.length,
      totalOdu: data.reduce((sum, i) => sum + (Number(i.Jumlah_ODU_NE) || 0) + (Number(i.Jumlah_ODU_FE) || 0), 0),
      totalIdu: data.reduce((sum, i) => sum + (Number(i.Volume_IDU_NE) || 0) + (Number(i.Volume_IDU_FE) || 0), 0),
      activeTregs: new Set(data.map(i => i.TREG).filter(Boolean)).size
    };
  }, [data]);
  const chartData = useMemo(() => {
    if (!data.length) return {
      treg: [],
      protection: [],
      freq: [],
      equipment: []
    };
    const tregMap = new Map();
    const protectionMap = new Map();
    const freqMap = new Map();
    let totalOdu = 0;
    let totalIdu = 0;
    let totalAntenna = 0;
    let totalModem = 0;
    data.forEach(i => {
      if (i.TREG) tregMap.set(i.TREG, (tregMap.get(i.TREG) || 0) + 1);
      if (i.Protection) protectionMap.set(i.Protection, (protectionMap.get(i.Protection) || 0) + 1);
      if (i.FreqGrouping) freqMap.set(i.FreqGrouping, (freqMap.get(i.FreqGrouping) || 0) + 1);
      totalOdu += (Number(i.Jumlah_ODU_NE) || 0) + (Number(i.Jumlah_ODU_FE) || 0);
      totalIdu += (Number(i.Volume_IDU_NE) || 0) + (Number(i.Volume_IDU_FE) || 0);
      totalAntenna += (Number(i.Volume_Antenna_NE) || 0) + (Number(i.Volume_Antenna_FE) || 0);
      totalModem += (Number(i.Volume_Modem_IDU_NE) || 0) + (Number(i.Volume_Modem_IDU_FE) || 0);
    });
    return {
      treg: Array.from(tregMap, ([name, value]) => ({
        name,
        value
      })).sort((a, b) => b.value - a.value).slice(0, 10),
      protection: Array.from(protectionMap, ([name, value]) => ({
        name,
        value
      })).sort((a, b) => b.value - a.value).slice(0, 8),
      freq: Array.from(freqMap, ([name, value]) => ({
        name,
        value
      })).sort((a, b) => b.value - a.value),
      equipment: [{
        name: 'ODU',
        value: totalOdu
      }, {
        name: 'IDU',
        value: totalIdu
      }, {
        name: 'Antenna',
        value: totalAntenna
      }, {
        name: 'Modem',
        value: totalModem
      }]
    };
  }, [data]);
  if (error) {
    return <div className="p-6 text-red-400 bg-red-950/30 rounded-xl border border-red-900" data-id="element-913" data-mp="e1af18da8fh2">
        Error: {String(error)}
      </div>;
  }
  return <div className="space-y-6" data-id="element-914" data-mp="94k0wql1ftya">
      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-915" data-mp="o0j71vol7k0s">
        {loading || !kpis ? Array(4).fill(0).map((_, i) => <CardSkeleton key={i} data-id="element-916" data-mp="j39kqowhvoh6" data-mpi={i} />) : <>
            <KPICard title="Total Sites" value={kpis.totalSites} icon={RadioTowerIcon} data-id="element-917" data-mp="wzd7jx51gujm" />
            <KPICard title="Total ODU" value={kpis.totalOdu} icon={ServerIcon} data-id="element-918" data-mp="16qhoh2e9yph3" />
            <KPICard title="Total IDU" value={kpis.totalIdu} icon={ActivityIcon} data-id="element-919" data-mp="11r4u7wl2qg4h" />
            <KPICard title="Active TREG" value={kpis.activeTregs} icon={MapIcon} data-id="element-920" data-mp="1frficrv01gly" />
          </>}
      </div>

      {/* CHART */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-921" data-mp="357rubx3q2kcb">
        {loading ? Array(4).fill(0).map((_, i) => <ChartSkeleton key={i} data-id="element-922" data-mp="3eycrdg3qzgt4" data-mpi={i} />) : <>
            {/* TREG */}
            <div className="glass-panel p-6 rounded-xl h-96" data-id="element-923" data-mp="3jx7jx9riojqf">
              <h3 className="text-sm font-semibold text-slate-100 mb-4 tracking-wide uppercase" data-id="element-924" data-mp="3jwo3xylfg5f1">
                Top TREG by Site Count
              </h3>
              <ResponsiveContainer data-id="element-925" data-mp="2r80a3emm47pv">
                <BarChart data={chartData.treg} data-id="element-926" data-mp="2r91z6n0gquql">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-927" data-mp="2w7v1djaauzbg" />
                  <XAxis dataKey="name" stroke="#cbd5f5" data-id="element-928" data-mp="2w9pp15cyh5j8" />
                  <YAxis stroke="#cbd5f5" data-id="element-929" data-mp="30a9i9xuerp03" />
                  <Tooltip contentStyle={{
                backgroundColor: '#020617',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#e2e8f0'
              }} data-id="element-930" data-mp="306vhgu1e7nur" />
                  <Bar dataKey="value" fill="#22d3ee" radius={[6, 6, 0, 0]} data-id="element-931" data-mp="2034g3azgeijj" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* PROTECTION */}
            <div className="glass-panel p-6 rounded-xl h-96" data-id="element-932" data-mp="1r6vhtzn7ppyz">
              <h3 className="text-sm font-semibold text-slate-100 mb-4 tracking-wide uppercase" data-id="element-933" data-mp="1maes510d73js">
                Protection Distribution
              </h3>
              <ResponsiveContainer data-id="element-934" data-mp="1h85ye2fo3xgm">
                <BarChart data={chartData.protection} layout="vertical" data-id="element-935" data-mp="1cb63mno9qh4t">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-936" data-mp="189ubobhx9jtg" />
                  <XAxis type="number" stroke="#cbd5f5" data-id="element-937" data-mp="18byc2jgm40oz" />
                  <YAxis dataKey="name" type="category" stroke="#cbd5f5" data-id="element-938" data-mp="13bix5ngoml4w" />
                  <Tooltip contentStyle={{
                backgroundColor: '#020617',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#e2e8f0'
              }} data-id="element-939" data-mp="32nlfgudkl9gz" />
                  <Bar dataKey="value" fill="#38bdf8" radius={[0, 8, 8, 0]} data-id="element-940" data-mp="3lk3ttghk5wsf" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* FREQUENCY */}
            <div className="glass-panel p-6 rounded-xl h-96" data-id="element-941" data-mp="37qw2lpxe33b9">
              <h3 className="text-sm font-semibold text-slate-100 mb-4 tracking-wide uppercase" data-id="element-942" data-mp="3cslbayx534w6">
                Frequency Distribution
              </h3>
              <ResponsiveContainer data-id="element-943" data-mp="8dbsboramz1s">
                <BarChart data={chartData.freq.slice(0, 8)} layout="vertical" data-id="element-944" data-mp="8caitopxekm6">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-945" data-mp="3qjzy7gu63q8i" />
                  <XAxis type="number" stroke="#cbd5f5" data-id="element-946" data-mp="3vibarzbukeuf" />
                  <YAxis dataKey="name" type="category" stroke="#cbd5f5" data-id="element-947" data-mp="3vgqd40u8kfsc" />
                  <Tooltip contentStyle={{
                backgroundColor: '#020617',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#e2e8f0'
              }} data-id="element-948" data-mp="2f306onfn9ffj" />
                  <Bar dataKey="value" fill="#fbbf24" radius={[0, 8, 8, 0]} data-id="element-949" data-mp="27lbqoghputmr">
                    <LabelList dataKey="value" position="right" fill="#e2e8f0" data-id="element-950" data-mp="2lk1qi3vx77et" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* EQUIPMENT */}
            <div className="glass-panel p-6 rounded-xl h-96" data-id="element-951" data-mp="1ouaug07zdc6w">
              <h3 className="text-sm font-semibold text-slate-100 mb-4 tracking-wide uppercase" data-id="element-952" data-mp="1ovvpdqy7ix5a">
                Equipment Composition
              </h3>
              <ResponsiveContainer data-id="element-953" data-mp="1xuh4fc4gzu1b">
                <BarChart data={chartData.equipment} data-id="element-954" data-mp="1xtefh9qpk1o1">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-955" data-mp="1a4mr6tg8o3gj" />
                  <XAxis dataKey="name" stroke="#cbd5f5" data-id="element-956" data-mp="1561siygmxru7" />
                  <YAxis stroke="#cbd5f5" data-id="element-957" data-mp="155ilwdwfqpvh" />
                  <Tooltip contentStyle={{
                backgroundColor: '#020617',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#e2e8f0'
              }} data-id="element-958" data-mp="kk8798kt3g5u" />
                  <Bar dataKey="value" fill="#a78bfa" radius={[6, 6, 0, 0]} data-id="element-959" data-mp="tn9a90edkr5a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>}
      </div>

      {/* TABLE tetap */}
      <div className="glass-panel rounded-xl p-4" data-id="element-960" data-mp="17kfr2pfjgfrv">
        <h3 className="text-sm font-semibold mb-4 text-slate-100" data-id="element-961" data-mp="17j4ouu1qrg2v">
          Recent Sites
        </h3>

        {recentLoading ? <TableSkeleton data-id="element-962" data-mp="1hahgbkjmo48d" /> : <table className="w-full text-sm text-slate-300" data-id="element-963" data-mp="1h9fr8c5s1h7n">
            <thead className="border-b border-slate-700 text-slate-400" data-id="element-964" data-mp="1m9kc0edpwy3o">
              <tr data-id="element-965" data-mp="1mbetfips6830">
                <th className="py-2 text-left" data-id="element-966" data-mp="1r8oigx99chqb">Site</th>
                <th className="py-2 text-left" data-id="element-967" data-mp="1r9ggeeqburjr">TREG</th>
                <th className="py-2 text-left" data-id="element-968" data-mp="3bqfvs7idn9uc">Freq</th>
              </tr>
            </thead>
            <tbody data-id="element-969" data-mp="36rmiaf33biu6">
              {recentData.map((item, i) => <tr key={i} className="border-b border-slate-800" data-id="element-970" data-mp="36qbc68m69yzw" data-mpi={i}>
                  <td data-id="element-971" data-mp="31tkw3ladb3eh">{item.Site_ID_NE}</td>
                  <td data-id="element-972" data-mp="31ucu12rftd7x">{item.TREG}</td>
                  <td data-id="element-973" data-mp="2wu8b763246ej">{item.FreqGrouping}</td>
                </tr>)}
            </tbody>
          </table>}
      </div>
    </div>;
}